import { NgClass } from '@angular/common';
import { Component, CUSTOM_ELEMENTS_SCHEMA, Inject } from '@angular/core';
import { FormsModule } from '@angular/forms';

import { L10n } from '@skylab-angular-shared/core';
import { TranslatePipe } from '@skylab-angular-shared/pipes';
import { ButtonComponent, DialogContentComponentBase, DialogContentComponentIns, DialogHeaderComponentIns, DialogInsService, DialogState, FocusByDirective, FormContainerComponent, NumericValueAccessorDirective, SelectValueAccessorDirective, ToastService, TooltipDirective, IconComponent } from '@skylab-angular-shared/ui';
import { IValueConverter, L10nBase } from '@skylab-shared/core';
import { isNumber } from '@skylab-shared/utils';

export enum ArrearageValueType {
    To = 'To',
    Range = 'Range',
}

export interface ArrearageValueRangeDialogResult {
    from: number;
    to: number;
}

interface ArrearageValueRangeDialogViewModel {
    to: number;
    range: {
        from: number;
        to: number;
    };
    isValueToInvalid: () => boolean;
    type: {
        value: ArrearageValueType
    };
    types: { label: string; value: ArrearageValueType }[];
    typeConverter: IValueConverter<string, { label: string; value: string; }>;
    isRangeVisible(): boolean;
}

@Component({
    selector: 'skl-arrearage-value-range-dialog',
    templateUrl: './arrearage-value-range-dialog.component.html',
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    imports: [DialogContentComponentIns, DialogHeaderComponentIns, FormContainerComponent, NgClass, FocusByDirective, SelectValueAccessorDirective, FormsModule, NumericValueAccessorDirective, TooltipDirective, ButtonComponent, TranslatePipe, IconComponent]
})
export class ArrearageValueRangeDialogComponent extends DialogContentComponentBase<ArrearageValueRangeDialogViewModel, ArrearageValueRangeDialogResult, ArrearageValueRangeDialogResult>  {
    public constructor(
        dialogService: DialogInsService,
        protected readonly toastService: ToastService,
        @Inject(L10n) private readonly l10n: L10nBase
    ) {
        super(dialogService);
    }

    public dayText(value: number) {
        return value === 1
            ? this.l10n._T('scenes.shared.dialog.arrearage-value-range-dialog.form.chosen-value.day')
            : this.l10n._T('scenes.shared.dialog.arrearage-value-range-dialog.form.chosen-value.days');
    }

    protected override getInitialState(): ArrearageValueRangeDialogViewModel {
        const self = this;
        const initialFromValue = 0;
        const initialToValue = 10;
        let type = isNumber(this.data) ? ArrearageValueType.To : ArrearageValueType.Range;

        return {
            ...super.getInitialState(),
            types: this.getTypes(),
            isRangeVisible: () => {
                return self.viewModel?.type.value === ArrearageValueType.Range;
            },
            to: isNumber(this.data) ? this.data : initialToValue,
            range: {
                from: !isNumber(this.data) && this.data?.from || initialFromValue,
                to: !isNumber(this.data) && this.data?.to || initialToValue,
            },
            isValueToInvalid: () => {
                return this.viewModel.range.to < this.viewModel.range.from;
            },
            type: {
                get value() {
                    return type;
                },
                set value(value) {
                    type = value;
                }
            },
            typeConverter: {
                convert: (value: string) => {
                    return this.getTypes().find(item => item.value === value) || { label: '', value: ArrearageValueType.Range };
                },
                convertBack: (item: { label: string; value: string }) => {
                    return item.value;
                },
            }
        };
    }

    private getTypes(): { value: ArrearageValueType; label: string }[] {
        return [{
            value: ArrearageValueType.Range,
            label: this.l10n._T('scenes.shared.dialog.arrearage-value-range-dialog.form.types.values.range')
        },
        {
            value: ArrearageValueType.To,
            label: this.l10n._T('scenes.shared.dialog.arrearage-value-range-dialog.form.types.values.to')
        }];
    }

    public chooseValue (): void {
        if (this.viewModel.isRangeVisible() && this.viewModel.isValueToInvalid()) return;

        this.dialogService.close({
            state: DialogState.Confirm,
            data: this.viewModel.isRangeVisible() ? {
                from: this.viewModel.range.from,
                to: this.viewModel.range.to,
            } : this.viewModel.to
        });
    }
}

export function getArrearageInitValue(params: {arrearValueTo: number | null, arrearValueFrom: number | null}): number | {from: number; to: number} | null {
    if (params.arrearValueFrom != null && params.arrearValueTo != null) {
        return {from: params.arrearValueFrom, to: params.arrearValueTo};
    } else if (params.arrearValueTo != null) {
        return params.arrearValueTo;
    }
    return null;
}
